import { useCallback, useMemo, useState } from 'react';

import {
  Badge,
  Button,
  DatatableCell,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldError,
  FieldLabel,
  Input,
  Label,
  Spinner,
} from '@monksflow/monks-ui';
import { PlusIcon } from 'lucide-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  useCreateExternalToolCategory,
  useDeleteExternalToolCategoryWithCascade,
  useUpdateExternalToolCategory,
} from '@/entities/external-tool';
import { DeleteConfirmationModal } from '@/shared/ui';
import { TableRow } from '@/shared/ui';
import { Table } from '@/shared/ui/Table';
import { useTableData } from '@/shared/hooks/useTableData';
import type { ExternalToolCategoryDto, ExternalToolDto } from '@inference.engine/api-client/web';

import { RowMenu } from './RowMenu';
import { FormReseed } from './FormReseed';
import { BigSwitch } from '@/shared/ui/BigSwitch';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

// ─── Schema ──────────────────────────────────────────────────────────────────

// A factory, not a module-scope const: building the schema at import time would bake
// in whatever language was active then, and the messages could never follow a change.
const buildCategorySchema = (t: TFunction<'validation'>) =>
  z.object({
    title: z.string().min(1, t('admin.categoriesPanel.titleIsRequired')).trim(),
    isVisible: z.boolean(),
  });

type CategoryFormData = z.infer<ReturnType<typeof buildCategorySchema>>;

// ─── Constants ───────────────────────────────────────────────────────────────

const buildColumns = (t: TFunction<'admin'>) => [
  { key: 'title' as const, label: t('admin.categoriesPanel.title'), sortable: true },
  { key: 'isVisible' as const, label: t('admin.categoriesPanel.visible'), sortable: true },
  { key: 'id' as const, label: '', sortable: false, className: 'w-30' },
];

// ─── Component ───────────────────────────────────────────────────────────────

interface CategoriesPanelProps {
  allCategories: ExternalToolCategoryDto[];
  categoriesLoading: boolean;
  tools: ExternalToolDto[];
  /**
   * Controlled add-modal open flag lifted to the parent. The parent's header
   * "Add" button toggles this via `onAddOpenChange` — no imperative handler
   * registration required.
   */
  addOpen?: boolean;
  onAddOpenChange?: (open: boolean) => void;
  workspaceId?: string;
}

export const CategoriesPanel = ({
  allCategories,
  categoriesLoading,
  tools,
  addOpen = false,
  onAddOpenChange,
  workspaceId,
}: CategoriesPanelProps) => {
  const { t } = useTranslation('admin');
  const { t: tValidation } = useTranslation('validation');
  const columns = useMemo(() => buildColumns(t), [t]);

  const [modalOpenLocal, setModalOpenLocal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ExternalToolCategoryDto | null>(null);
  const [categoryToDelete, setCategoryToDelete] = useState<ExternalToolCategoryDto | null>(null);

  const isWorkspaceTab = !!workspaceId;
  const isOrganizationTab = !isWorkspaceTab;

  const createMutation = useCreateExternalToolCategory();
  const updateMutation = useUpdateExternalToolCategory();
  const deleteMutation = useDeleteExternalToolCategoryWithCascade();

  // Modal opens either from the parent-controlled `addOpen` (add flow) or from
  // the row-level Edit action (which drives `modalOpenLocal`).
  const modalOpen = modalOpenLocal || (addOpen && editingCategory === null);

  const openAddModal = () => {
    setEditingCategory(null);
    onAddOpenChange?.(true);
  };

  const tableData = useTableData({
    data: allCategories,
    initialSort: { key: 'title' as const, direction: 'asc' as const },
    initialRowsPerPage: 25,
    searchFields: ['title'] as Array<keyof ExternalToolCategoryDto>,
  });

  const formSchema = useMemo(() => buildCategorySchema(tValidation), [tValidation]);

  const form = useForm<CategoryFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: { title: '', isVisible: true },
  });

  // reseed the rhf form when the (editingCategory, modalOpen)
  // identity changes. `form.reset()` synchronously notifies its subscribers, so
  // running it from the render body made the mounted `Controller` below call
  // setState during this panel's render. `<FormReseed>` (key-driven remount +
  // useMountEffect) moves it post-commit without a useEffect here.
  // eslint-disable-next-line i18next/no-literal-string -- React key discriminators, never rendered
  const formKey = `${editingCategory?.id ?? 'new'}|${modalOpen ? 'open' : 'closed'}`;
  const reseedForm = useCallback(() => {
    if (editingCategory) {
      form.reset({ title: editingCategory.title, isVisible: editingCategory.isVisible });
    } else {
      form.reset({ title: '', isVisible: true });
    }
  }, [editingCategory, form]);

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  const handleClose = () => {
    setModalOpenLocal(false);
    setEditingCategory(null);
    onAddOpenChange?.(false);
  };

  const handleSubmit = form.handleSubmit((data) => {
    const body = {
      title: data.title,
      isVisible: data.isVisible,
    };
    if (editingCategory) {
      updateMutation.mutate(
        { categoryId: editingCategory.id, body, workspaceId },
        { onSuccess: handleClose },
      );
    } else {
      createMutation.mutate({ body, workspaceId }, { onSuccess: handleClose });
    }
  });

  const affectedTools = categoryToDelete
    ? tools.filter((r) => r.categories.some((cat) => cat.id === categoryToDelete.id))
    : [];
  const affectedCount = affectedTools.length;
  const isOrgWideCategory = !categoryToDelete?.workspaceId;

  return (
    <>
      <FormReseed reseedKey={formKey} onReseed={reseedForm} />
      <div className="flex flex-col gap-6">
        {allCategories.length === 0 && !categoriesLoading ? (
          <div className="flex flex-col items-center gap-4 py-10">
            <h3 className="text-xl leading-7 text-foreground">
              {t('admin.categoriesPanel.noCategoriesYet')}
            </h3>
            <p className="text-sm leading-5 text-muted-foreground">
              {t('admin.categoriesPanel.categoriesHelpOrganizeExternalTools')}
            </p>
            <Button type="button" onClick={openAddModal}>
              <PlusIcon />
              {t('admin.categoriesPanel.addCategory')}
            </Button>
          </div>
        ) : (
          <Table<ExternalToolCategoryDto>
            isLoading={categoriesLoading}
            columns={columns}
            rows={tableData.paginatedData}
            sortBy={tableData.sortBy}
            sortDirection={tableData.sortDirection}
            onSort={tableData.toggleSort}
            pagination={{
              count: tableData.totalCount,
              page: tableData.page,
              rowsPerPage: tableData.rowsPerPage,
              onPageChange: tableData.handlePageChange,
              onRowsPerPageChange: tableData.handleRowsPerPageChange,
            }}
            renderBodyRow={(row) => {
              const isOrgWideRow = !row.workspaceId;
              const canEdit = !isOrgWideRow || isOrganizationTab;

              return (
                <TableRow key={row.id}>
                  <DatatableCell>
                    <div className="flex flex-row items-center gap-2">
                      <span className="text-sm font-medium leading-5">{row.title}</span>
                      {isOrgWideRow && (
                        <Badge variant="secondary">{t('admin.categoriesPanel.organization')}</Badge>
                      )}
                    </div>
                  </DatatableCell>
                  <DatatableCell>
                    <BigSwitch
                      disabled={!canEdit}
                      checked={row.isVisible}
                      aria-label={t('admin.categoriesPanel.visibleToUsers')}
                      onCheckedChange={(isVisible) =>
                        canEdit &&
                        updateMutation.mutate({
                          categoryId: row.id,
                          body: { isVisible },
                          workspaceId,
                        })
                      }
                    />
                  </DatatableCell>
                  <DatatableCell>
                    {canEdit && (
                      <RowMenu
                        onEdit={() => {
                          setEditingCategory(allCategories.find((c) => c.id === row.id) ?? null);
                          setModalOpenLocal(true);
                        }}
                        onDelete={() =>
                          setCategoryToDelete(allCategories.find((c) => c.id === row.id) ?? null)
                        }
                        deleteDisabled={deleteMutation.isPending}
                      />
                    )}
                  </DatatableCell>
                </TableRow>
              );
            }}
            containerClassName="p-0"
          />
        )}
      </div>

      <Dialog
        open={modalOpen}
        onOpenChange={(nextOpen) => {
          if (!nextOpen && !isSubmitting) handleClose();
        }}
      >
        <DialogContent className="gap-0 p-4" showCloseButton={!isSubmitting}>
          <DialogHeader>
            <DialogTitle>
              {editingCategory
                ? editingCategory.workspaceId
                  ? t('admin.categoriesPanel.editWorkspaceCategory')
                  : t('admin.categoriesPanel.editOrganizationCategory')
                : workspaceId
                  ? t('admin.categoriesPanel.addWorkspaceCategory')
                  : t('admin.categoriesPanel.addOrganizationCategory')}
            </DialogTitle>
          </DialogHeader>
          <DialogBody className="flex flex-col gap-6 py-4">
            <Field data-invalid={form.formState.errors.title ? true : undefined}>
              <FieldLabel htmlFor="category-name">
                {t('admin.categoriesPanel.categoryName')}
                <span aria-hidden="true" className="text-destructive">
                  *
                </span>
              </FieldLabel>
              <Input
                id="category-name"
                required
                placeholder={t('admin.categoriesPanel.enterCategoryName')}
                aria-invalid={!!form.formState.errors.title}
                {...form.register('title')}
              />
              {form.formState.errors.title && (
                <FieldError>{form.formState.errors.title.message}</FieldError>
              )}
            </Field>
            <Controller
              name="isVisible"
              control={form.control}
              render={({ field }) => (
                <div className="flex items-center gap-2">
                  <BigSwitch
                    id="category-is-visible"
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                  <Label htmlFor="category-is-visible">
                    {t('admin.categoriesPanel.visibleToUsers')}
                  </Label>
                </div>
              )}
            />
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
              {t('admin.categoriesPanel.cancel')}
            </Button>
            <Button type="button" onClick={() => void handleSubmit()} disabled={isSubmitting}>
              {/* Hidden from the a11y tree so the button keeps its own name. */}
              {isSubmitting && <Spinner aria-hidden="true" />}
              {editingCategory
                ? t('admin.categoriesPanel.update')
                : t('admin.categoriesPanel.addCategory')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DeleteConfirmationModal
        open={categoryToDelete !== null}
        isDeleting={deleteMutation.isPending}
        onCancel={() => setCategoryToDelete(null)}
        onConfirmDelete={() => {
          if (!categoryToDelete) return;
          deleteMutation.mutate(
            {
              categoryId: categoryToDelete.id,
              toolIds: affectedTools.map((r) => r.id),
              workspaceId,
            },
            { onSuccess: () => setCategoryToDelete(null) },
          );
        }}
        resourceName={categoryToDelete?.title}
        warningMessage={
          affectedCount > 0
            ? isOrgWideCategory
              ? t('admin.categoriesPanel.thisWillPermanentlyDelete', { count: affectedCount })
              : t('admin.categoriesPanel.thisWillAlsoPermanentlyDelete', {
                  count: affectedCount,
                })
            : t('admin.categoriesPanel.thisActionCannotBeUndone')
        }
      />
    </>
  );
};
